import { AuthClient } from "@bcmi-labs/art-auth"
import {
  ARTThemeManager,
  defaultThemeManagerStorage,
  type IStorage,
  PALETTE_DEFAULT_KEY
} from "@bcmi-labs/art-ui/theming"
import { SpaceStorage } from "@bcmi-labs/cloud-sidebar"
import { QueryClient } from "@tanstack/react-query"

import { isDeterminateClientError } from "@/api/utils/NetworkError"
import { auth0Config, deploymentPath } from "@/config"
import { env } from "@/env"

/** Global queryClient */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // The server has answered about this resource; retrying cannot change it.
        if (isDeterminateClientError(error)) return false
        return failureCount < 2
      },
      staleTime: 20000 // 20 seconds
    }
  }
})

// Create a new instance of the AuthClient
export const AuthClientInstance = deploymentPath.customKey
  ? await AuthClient.createCloudClient(auth0Config, deploymentPath.customKey, {
      API_URL: env.VITE_API2_URL
    }).catch(() =>
      // Bailing out! We can't create the client, so we redirect to the default app domain (which will error out).
      AuthClient.create(auth0Config)
    )
  : AuthClient.create(auth0Config)

// Crete a new instance of the space storage
export const SpaceStorageInstance = new SpaceStorage(
  env.VITE_COOKIES_DOMAIN,
  // Try binding the space storage to the customer space if we have a fourth level domain. Otherwise, fallback to the default domain.
  deploymentPath.customKey ? `${deploymentPath.customKey}-arduino` : undefined
)

export const themeManagerInstance = (() => {
  /*
   * ==========================================
   * Custom palettes in Arduino Cloud are a function of space-id, so we scope the palette storage
   * to the space-id as well.
   * To achieve this, we namespace the palette storage entries under the `${PALETTE_DEFAULT_KEY}:${spaceId}` key.
   *
   * This way, different spaces can have different palettes and they are restored correctly on load without flash.
   * ==========================================
   */
  const spaceScopedStorage: IStorage = (() => {
    // Getter function for stable key for the palette storage, based on the current space.
    const getPaletteKey = () => `${PALETTE_DEFAULT_KEY}:${deploymentPath.customKey || SpaceStorageInstance.getSpace()}`

    const storageInterface: IStorage = {
      get: key => {
        if (key !== PALETTE_DEFAULT_KEY) return defaultThemeManagerStorage.get(key)
        return defaultThemeManagerStorage.get(getPaletteKey())
      },
      set: (key, value) => {
        if (key !== PALETTE_DEFAULT_KEY) return defaultThemeManagerStorage.set(key, value)
        return defaultThemeManagerStorage.set(getPaletteKey(), value)
      }
    }
    return storageInterface
  })()

  const manager = new ARTThemeManager("auto", spaceScopedStorage)

  // Whitelabel environments can rely on the palette without needinga storage layer.
  if (deploymentPath.customKey) {
    const customization = AuthClientInstance.getCustomization()
    if (!customization) {
      throw new Error("Something went wrong: customization is not available for whitelabel environment.")
    }

    manager.setPalette(
      customization.colors?.color
        ? {
            hex: customization.colors.color as `#${string}`,
            useClosestAccessibleColor: customization.colors.readability ?? false
          }
        : "mkr"
    )
  }

  return manager
})()
